import { useState } from "react";

import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { postIntelImageMatch } from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";

import {
  useImageMatch,
  MatchSourceAttachment,
  MatchTargetAttachment,
} from "@/contexts/ImageMatchContext";

interface SaveMatchesBeforeEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceedToEdit: () => void;
}

export const SaveMatchesBeforeEditModal = ({
  isOpen,
  onClose,
  onProceedToEdit,
}: SaveMatchesBeforeEditModalProps) => {
  const { t } = useTranslation("reportsv2/condensed_report");
  const { sourceAttachment, targetAttachments, completeMatch, cancelMatch } =
    useImageMatch();
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveAndEdit = async () => {
    if (!sourceAttachment || targetAttachments.length === 0) {
      // No targets selected — just cancel the match and proceed
      cancelMatch();
      onProceedToEdit();
      return;
    }

    setIsSaving(true);
    try {
      await Promise.all(
        targetAttachments.map((target: MatchTargetAttachment) =>
          postIntelImageMatch({
            body: {
              submission_id_1: (sourceAttachment as MatchSourceAttachment)
                .submissionId,
              attachment_id_1: (sourceAttachment as MatchSourceAttachment)
                .attachmentId,
              submission_id_2: target.submissionId,
              attachment_id_2: target.attachmentId,
              notes: null,
            },
          }),
        ),
      );
      toast.success(
        `Match created with ${targetAttachments.length} attachment(s)`,
      );
      completeMatch();
      onProceedToEdit();
    } catch (error) {
      console.error("Failed to save image matches:", error);
      toast.error("Failed to save image matches. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardAndEdit = () => {
    cancelMatch();
    onProceedToEdit();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby="save-matches-before-edit-dialog-title"
      aria-describedby="save-matches-before-edit-dialog-description"
    >
      <DialogTitle id="save-matches-before-edit-dialog-title">
        {t("save_matches_before_edit_title")}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="save-matches-before-edit-dialog-description">
          {t("save_matches_before_edit_description")}
        </DialogContentText>
        {targetAttachments.length > 0 && (
          <DialogContentText sx={{ mt: 2 }}>
            {t("save_matches_before_edit_count", {
              count: targetAttachments.length,
            })}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "16px",
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          color="secondary"
          variant="outlined"
          disabled={isSaving}
        >
          {t("cancel")}
        </Button>
        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            onClick={handleDiscardAndEdit}
            color="warning"
            variant="outlined"
            disabled={isSaving}
          >
            {t("discard_matches_and_edit")}
          </Button>
          <LoadingButton
            onClick={handleSaveAndEdit}
            color="primary"
            variant="contained"
            loading={isSaving}
            disabled={isSaving}
          >
            {targetAttachments.length > 0
              ? t("save_matches_and_edit")
              : t("save_and_continue")}
          </LoadingButton>
        </div>
      </DialogActions>
    </Dialog>
  );
};
